/**
 * Script Edit Utilities - MA-ARCH-163
 * 
 * Extracted from ScriptEdit.jsx - Helper functions for node display, token handling, and formulas.
 */

/**
 * Generate a label for a node from its title or first snippet of reply
 */
export const getNodeLabel = (n) => {
  const t = (n?.title || "").trim();
  if (t) return t;
  const snippet = (n?.your_reply || "").trim().slice(0, 38);
  return snippet ? `${snippet}${(n?.your_reply || "").length > 38 ? "…" : ""}` : "Untitled";
};

/**
 * Generate a preview text for a node from script_blocks or your_reply
 */
export const getNodePreviewText = (n) => {
  const sbs = Array.isArray(n?.script_blocks) ? n.script_blocks : [];

  // Find first meaningful text from script blocks
  let text = "";
  for (const sb of sbs) {
    const blocks = Array.isArray(sb?.blocks) ? sb.blocks : [];
    for (const b of blocks) {
      if (!b) continue;
      if (b.type === "text") {
        const t = String(b.text || "").trim();
        if (t) {
          text = t;
          break;
        }
      }
    }
    if (text) break;
  }

  const src = (text || (n?.your_reply || "")).trim();
  if (!src) return "(empty)";
  const snippet = src.slice(0, 120);
  return snippet ? `${snippet}${src.length > 120 ? "…" : ""}` : "(empty)";
};

/**
 * Move an item in an array from one index to another
 */
export const moveItem = (items, fromIdx, toIdx) => {
  if (toIdx < 0 || toIdx >= items.length) return items;
  const next = [...items];
  const [it] = next.splice(fromIdx, 1);
  next.splice(toIdx, 0, it);
  return next;
};

/**
 * Quote field type labels for display
 */
export const QUOTE_FIELD_TYPE_LABELS = {
  text: "Text",
  textarea: "Long text",
  dropdown: "Drop down",
  number: "Number",
};

/**
 * Data field type labels for display
 */
export const DATA_FIELD_TYPE_LABELS = {
  text: "Text",
  phone: "Phone",
  email: "Email",
  number: "Number",
  textarea: "Long text",
  checkbox: "Checkbox",
  dropdown: "Dropdown",
};

/**
 * Convert a formula token to display text
 */
export const quoteTokenToText = (t) => {
  if (!t) return "";
  if (t.type === "field") return `{${t.label}}`;
  if (t.type === "sum_all") return `Σ{${t.label}}`;
  if (t.type === "number") return String(t.value);
  if (t.type === "op") return t.op;
  if (t.type === "lparen") return "(";
  if (t.type === "rparen") return ")";
  return "";
};

/**
 * Render a formula as a string
 */
export const renderQuoteFormula = (tokens) => {
  if (!Array.isArray(tokens) || tokens.length === 0) return "";
  return tokens.map(quoteTokenToText).join(" ").replace(/\s+\)/g, ")").replace(/\(\s+/g, "(");
};

/**
 * Quote sheet label presets
 */
export const QUOTE_LABEL_PRESETS = [
  "Company",
  "Product",
  "Insurance Type",
  "Monthly Premium",
  "Death Benefit",
  "Cash Value Projection",
  "Critical Illness Benefit",
  "Chronic Illness Benefit",
];

/**
 * Apply auto-prospect assignment to script blocks
 */
export const applyAutoProspectAssignment = (blocks) => {
  if (!Array.isArray(blocks)) return blocks;

  // Count occurrences of each data field across all Prospect-scoped Script Blocks.
  const counts = {};

  return blocks.map((sb) => {
    const sbScope = sb?.scope || "household";
    if (sbScope !== "prospect") return sb;

    const nextBlocks = (sb.blocks || []).map((b) => {
      if (!b || b.type !== "field") return b;
      const fid = b.field_id;
      if (!fid) return b;

      const existing = String(b.prospect_scope || "").trim();
      const hasAuto = Number.isFinite(Number(b.auto_prospect_index));
      const isLegacyPinned = existing === "prospect_1" && !hasAuto;

      if (existing && existing !== "household" && !isLegacyPinned) {
        return b;
      }

      const nextCount = (counts[fid] || 0) + 1;
      counts[fid] = nextCount;

      return {
        ...b,
        prospect_scope: `prospect_${nextCount}`,
        auto_prospect_index: nextCount,
      };
    });

    return { ...sb, blocks: nextBlocks };
  });
};
